import { supabase } from '../../core/config/supabase';
import { TournamentInvitationEntity, TournamentInvitationWithDetailsEntity, TournamentEntity } from '../entities';
import { logger } from '@/src/core/utils';

export class TournamentInvitationDataSource {
  async create(
    tournamentId: string,
    inviterId: string,
    inviteeId: string,
    message?: string
  ): Promise<TournamentInvitationEntity> {
    const { data, error } = await supabase
      .from('tournament_invitations')
      .insert({
        tournament_id: tournamentId,
        inviter_id: inviterId,
        invitee_id: inviteeId,
        message,
        status: 'pending',
      })
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from insert');

    return data as TournamentInvitationEntity;
  }

  async update(
    invitationId: string,
    status: 'accepted' | 'declined' | 'cancelled'
  ): Promise<TournamentInvitationEntity> {
    const { data, error } = await supabase
      .from('tournament_invitations')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('id', invitationId)
      .select()
      .single();

    if (error) throw new Error(error.message);
    if (!data) throw new Error('No data returned from update');

    return data as TournamentInvitationEntity;
  }

  async getReceived(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<TournamentInvitationWithDetailsEntity[]> {
    logger.debug('InvitationDataSource', 'Calling RPC get_invitations_received');

    const { data, error } = await supabase.rpc('get_invitations_received', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('InvitationDataSource', 'getReceived failed', { error });
      throw new Error(error.message);
    }

    return (data as TournamentInvitationWithDetailsEntity[]) || [];
  }

  async getSent(
    userId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<TournamentInvitationWithDetailsEntity[]> {
    logger.debug('InvitationDataSource', 'Calling RPC get_invitations_sent');

    const { data, error } = await supabase.rpc('get_invitations_sent', {
      target_user_id: userId,
      limit_count: limit,
      offset_count: offset,
    });

    if (error) {
      logger.error('InvitationDataSource', 'getSent failed', { error });
      throw new Error(error.message);
    }

    return (data as TournamentInvitationWithDetailsEntity[]) || [];
  }

  async getByTournamentAndUser(
    tournamentId: string,
    inviteeId: string
  ): Promise<TournamentInvitationEntity | null> {
    const { data, error } = await supabase
      .from('tournament_invitations')
      .select()
      .eq('tournament_id', tournamentId)
      .eq('invitee_id', inviteeId)
      .eq('status', 'pending')
      .maybeSingle();

    if (error) throw new Error(error.message);
    return data as TournamentInvitationEntity | null;
  }

  async getTournamentByInviteCode(code: string): Promise<TournamentEntity | null> {
    const { data, error } = await supabase.rpc('get_tournament_by_invite_code', {
      code,
    });

    if (error) {
      logger.error('InvitationDataSource', 'getTournamentByInviteCode failed', { error });
      throw new Error(error.message);
    }

    const results = data as TournamentEntity[];
    return results && results.length > 0 ? results[0] : null;
  }

  /**
   * Validation (code, status, duplicates, capacity/plan limits) and the participant
   * insert run in the database for auth.uid(); RLS doesn't let users insert themselves.
   * Errors keep the server message ('Invalid invite code', 'Tournament is full'…).
   */
  async joinTournamentByCode(code: string, userName: string): Promise<TournamentEntity> {
    const { data, error } = await supabase.rpc('join_tournament_by_code', {
      p_code: code,
      p_name: userName,
    });

    if (error) throw new Error(error.message);

    const results = data as TournamentEntity[];
    if (!results || results.length === 0) throw new Error('Invalid invite code');
    return results[0];
  }

  async generateInviteCode(tournamentId: string): Promise<string> {
    const code = this.createRandomCode();

    const { error } = await supabase
      .from('tournaments')
      .update({ invite_code: code })
      .eq('id', tournamentId);

    if (error) throw new Error(error.message);

    return code;
  }

  private createRandomCode(): string {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // No I/O/0/1 to avoid confusion
    let code = '';
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  }
}
